<template>
  <div class="sp-broadcast" :class="classModifiers">
    <div class="sp-broadcast__content">
      <sp-icon v-if="hasIcon" class="sp-broadcast__icon" :name="icon" :fill-color="iconFillColor"></sp-icon>

      <div class="sp-broadcast__body">
        <div
          v-show="hasTitle"
          class="sp-broadcast__title"
          v-bind="titleToggleAttributes"
          @click="toggleOnClick"
          @keydown="toggleOnActivationKey"
        >
          <slot ref="titleSlot" name="title" />
        </div>
        <div v-if="!isCollapsable" class="sp-broadcast__text">
          <slot />
        </div>
      </div>

      <div v-show="hasAction" class="sp-broadcast__actions">
        <div class="sp-broadcast__actions-row">
          <slot ref="actionSlot" name="action" />
        </div>
      </div>

      <div v-if="isDismissable" class="sp-broadcast__dismiss">
        <sp-button
          class="sp-broadcast__dismiss-button"
          icon="close"
          size="small"
          variant="text"
          color="text"
          densitysmcompact="false"
          :label="dismissLabel"
          @click="dismissBroadcast"
        ></sp-button>
      </div>
    </div>

    <div v-if="isCollapsable" v-show="isExpanded" class="sp-broadcast__text --below">
      <slot />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, shallowRef, useHost, useTemplateRef, watch } from "vue";
import { useEventListener } from "@vueuse/core";
import { crossedInteractiveContent } from "#ce/utils/events";
import { toBoolean } from "#ce/utils/props";
import fallbackMessages from "#/shared/i18n/frontend.broadcast.json";
import { props as broadcastProps } from "./props.js";

const props = defineProps(broadcastProps);

const host = useHost();

const hasIcon = computed(() => Boolean(props.icon) && props.icon !== "none-filled");
const iconFillColor = computed(() => `var(--sp-comp-broadcast-message-level-${props.level}-icon-color)`);

const titleSlot = useTemplateRef("titleSlot");
const actionSlot = useTemplateRef("actionSlot");
const hasTitle = ref(false);
const hasAction = ref(false);

function measureTitle() {
  hasTitle.value = titleSlot.value?.assignedNodes().length > 0;
}

function measureAction() {
  hasAction.value = actionSlot.value?.assignedNodes().length > 0;
}

function measureSlots() {
  measureTitle();
  measureAction();
}

// The page can add or remove the title or the action after mount, as the finder does when it
// reuses a banner for the next response.
useEventListener(titleSlot, "slotchange", measureTitle);
useEventListener(actionSlot, "slotchange", measureAction);
onMounted(measureSlots);

// The title is the toggle, so a broadcast without one stays expanded whatever `collapsable` says.
const isCollapsable = computed(() => toBoolean(props.collapsable) && hasTitle.value);
const isExpanded = ref(false);

const ACTIVATION_KEYS = ["Enter", " "];

function toggleExpanded() {
  if (isCollapsable.value) {
    isExpanded.value = !isExpanded.value;
  }
}

// The title is WYSIWYG content, so a link or button inside it keeps its own click.
function toggleOnClick(event) {
  if (crossedInteractiveContent(event, event.currentTarget)) {
    return;
  }

  toggleExpanded();
}

// Enter/Space are claimed only while the title is the toggle, and never from a link or button
// inside it. The default is prevented on every claimed press so a held Space never scrolls the
// page. The state changes once per press, not per auto-repeat.
function toggleOnActivationKey(event) {
  if (!isCollapsable.value || !ACTIVATION_KEYS.includes(event.key)) {
    return;
  }

  if (crossedInteractiveContent(event, event.currentTarget)) {
    return;
  }

  event.preventDefault();

  if (!event.repeat) {
    toggleExpanded();
  }
}

// The title is the toggle when the broadcast collapses, so it carries button semantics.
const titleToggleAttributes = computed(() => {
  if (!isCollapsable.value) {
    return {};
  }

  return { role: "button", tabindex: 0, "aria-expanded": String(isExpanded.value) };
});

const isDismissable = computed(() => toBoolean(props.dismissable));
const translate = shallowRef();
const dismissLabel = computed(() => translate.value?.("dismiss") ?? fallbackMessages.dismiss);

// Imports `./i18n.js` the first time the broadcast is dismissable, so a non-dismissable broadcast
// never loads the i18n library or fetches the strings. The lazy build bundles this element into
// the boot script every page runs. The English label stands in until the strings arrive.
async function loadDismissLabel(dismissable) {
  if (!dismissable || translate.value) {
    return;
  }

  const { t } = await import("./i18n.js");
  translate.value = t;
}

watch(isDismissable, loadDismissLabel, { immediate: true });

function dismissBroadcast() {
  host.hidden = true;
}

const classModifiers = computed(() => ({
  "--is-inline": toBoolean(props.inline) && !isCollapsable.value,
  "--is-collapsable": isCollapsable.value,
  "--is-expanded": isExpanded.value,
  "--has-action": hasAction.value,
}));
</script>

<style lang="scss">
@use "./box";

@include box.box(shadow);
</style>

<style lang="scss" scoped>
@use "./box";

// The banner's text sits in the host's content box (see `_box.scss`). The icon sits in the host's
// start padding and the dismiss button in its end padding, by default both centered on the first
// line.
.sp-broadcast {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  min-block-size: var(--broadcast-min-content);

  // Fills the content box. With `--sp-comp-broadcast-message-align: center` it shrinks to its text
  // and centers, taking the icon and the dismiss button with it.
  &__content {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    justify-self: var(--sp-comp-broadcast-message-align, stretch);
  }

  // The action column reuses the gap the host keeps at its end, so the action narrows the text by
  // its own width only. The dismiss button follows the action.
  &.--has-action &__content {
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: var(--broadcast-gap);
    margin-inline-end: calc(-1 * var(--broadcast-gap));
  }

  // Sized here, so the icon's box has its final size before `sp-icon` loads.
  &__icon {
    --sp-comp-icon-size: var(--broadcast-icon-size);
    position: absolute;
    inset-block-start: calc(
      var(--broadcast-icon-offset) - var(--broadcast-content-padding-top) - var(--broadcast-push)
    );
    inset-inline-start: calc(-1 * var(--broadcast-icon-column));
    inline-size: var(--broadcast-icon-size);
    block-size: var(--broadcast-icon-size);
  }

  &__title {
    @include box.title;
  }

  // Centers the action on the first line, in a box that extends as far above the line as below it
  // without reaching into the level and variant padding. An action taller than the box starts at its
  // top and grows the banner.
  &__actions {
    --broadcast-action-reach: calc(var(--broadcast-content-padding-top) + var(--broadcast-push));
    --broadcast-action-inset: max(0px, var(--broadcast-action-reach) - var(--broadcast-content-padding-bottom));
    display: flex;
    flex-direction: column;
    align-self: start;
    min-block-size: calc(var(--broadcast-line) + 2 * (var(--broadcast-action-reach) - var(--broadcast-action-inset)));
    margin-block: calc(-1 * var(--broadcast-action-reach));
    padding-block: var(--broadcast-action-inset);
  }

  // Auto margins center the row, and start it at the top of the box when it is taller.
  &__actions-row {
    display: flex;
    gap: var(--broadcast-gap);
    margin-block: auto;
  }

  // Sized here, so the dismiss button's box has its final size before `sp-button` loads.
  &__dismiss {
    position: absolute;
    inset-block-start: max(
      -1 * (var(--broadcast-content-padding-top) + var(--broadcast-push)),
      (var(--broadcast-line) - var(--broadcast-dismiss-size)) * 0.5
    );
    inset-inline-start: calc(100% + var(--broadcast-gap));
    display: flex;
    align-items: center;
    justify-content: center;
    inline-size: var(--broadcast-dismiss-size);
    block-size: var(--broadcast-dismiss-size);
  }

  &__dismiss-button {
    --sp-comp-button-small-height: var(--broadcast-dismiss-size);
    --sp-comp-button-icon-fill-color: var(--sp-comp-broadcast-message-actions-dismiss-color, #000);
  }

  // The title runs into the text. These rules put only the body's `.broadcast-message-text` wrapper
  // inline. On the page, `_light-dom.scss` also puts the paragraphs and blocks inside it inline. No
  // page rule reaches them inside another element's shadow root.
  &.--is-inline {
    .sp-broadcast__title {
      @include box.inline-title;
    }

    .sp-broadcast__text {
      display: inline;
    }

    .sp-broadcast__text ::slotted(.broadcast-message-text) {
      display: inline;
    }
  }

  // The title toggles the body, which opens below the row of the icon, the title and the dismiss
  // button.
  &.--is-collapsable {
    .sp-broadcast__title {
      @include box.collapsable-title;
      position: relative;
      inline-size: fit-content;
      max-inline-size: 100%;
      cursor: pointer;

      &::after {
        position: absolute;
        inset-block-start: 0.475em;
        inset-inline-end: 0.3rem;
        border-style: solid;
        border-width: 0.3em 0.3em 0;
        border-color: var(--broadcast-text-color, #000) transparent transparent;
        content: "";
        transition: transform 0.2s ease-in-out;
      }
    }

    &.--is-expanded .sp-broadcast__title::after {
      transform: rotate(180deg);
    }
  }

  // Inset by the icon's column at both ends: it starts under the title, and a centered body stays
  // centered under the title row.
  &__text.--below {
    margin-block-start: calc(var(--broadcast-content-padding-bottom) + var(--sp-ref-scale-8));
    margin-inline-end: calc(var(--broadcast-icon-column) - var(--broadcast-dismiss-column));
  }
}
</style>
